import React, { useState, useMemo } from 'react';
import {
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Menu,
  MenuItem
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckIcon from '@material-ui/icons/Check';
import swal from 'sweetalert';

import TableComponent from '../../Layouts/TableComponent';
import SubTaskModal from './SubTaskModal';
import {
  parseTimelineHoursMinutes,
  TimelineSegmentedCell
} from './TimelineInputField';
import { resolveImageUrl } from '../../../utils/imageUrl';
import defaultAvatar from '../../../Images/person.jpg';

const SUBTASK_TABLE_HEADERS = [
  { id: 'sub_task_id', numeric: false, disablePadding: false, label: 'Subtask ID', sortable: false, style: { width: '120px', minWidth: '100px', maxWidth: '130px', textAlign: 'center' } },
  { id: 'priority', numeric: false, disablePadding: false, label: 'Priority', sortable: false, style: { width: '115px', minWidth: '110px', maxWidth: '125px', textAlign: 'center' } },
  { id: 'task', numeric: false, disablePadding: false, label: 'Subtask Name', sortable: false, style: { minWidth: '200px', textAlign: 'left' } },
  { id: 'assigned_developer', numeric: false, disablePadding: false, label: 'Assign Developer', sortable: false, style: { width: '150px', minWidth: '135px', maxWidth: '165px', whiteSpace: 'nowrap', textAlign: 'center' } },
  { id: 'timeline', numeric: false, disablePadding: false, label: 'Timeline', sortable: false, style: { width: '120px', minWidth: '110px', maxWidth: '130px', textAlign: 'center' } },
  { id: 'status', numeric: false, disablePadding: false, label: 'Status', sortable: false, style: { width: '125px', minWidth: '120px', maxWidth: '135px', textAlign: 'center' } },
  { id: 'actions', numeric: false, disablePadding: false, label: 'Actions', sortable: false, style: { width: '110px', minWidth: '100px', maxWidth: '120px', textAlign: 'center' } }
];

const parseTimelineMinutes = (val) => {
  const parsed = parseTimelineHoursMinutes(val);
  if (!parsed) return 0;
  return (parsed.h || 0) * 60 + (parsed.m || 0);
};

const PRIORITY_MENU_OPTIONS = [
  { label: 'Urgent', color: '#dc2626' },
  { label: 'High', color: '#f97316' },
  { label: 'Medium', color: '#eab308' },
  { label: 'Low', color: '#10b981' }
];

const STATUS_MENU_OPTIONS = [
  { label: 'Backlog', color: '#64748b' },
  { label: 'Pending', display: 'To Do', color: '#0284c7' },
  { label: 'In Progress', display: 'In Progress', color: '#f59e0b' },
  { label: 'Testing', display: 'Testing', color: '#7c3aed' },
  { label: 'Done', display: 'Done', color: '#10b981' }
];

const SubTasksSection = ({
  subTasks = [],
  setSubTasks,
  users = [],
  allUsers = [],
  projectName = '',
  mainTaskTimeline = '',
  mainTaskStatus = '',
  parentTaskId = '',
  readOnly = false,
  title = 'Sub Tasks'
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit' | 'view'
  const [selectedIdx, setSelectedIdx] = useState(null);

  // Priority dropdown menu state for inline table click
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState(null);
  const [priorityMenuIndex, setPriorityMenuIndex] = useState(null);

  // Status dropdown menu state for inline table click
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuIndex, setStatusMenuIndex] = useState(null);

  const isBacklogMain = String(mainTaskStatus || '').trim().toLowerCase() === 'backlog';
  const isDoneMain = String(mainTaskStatus || '').trim().toLowerCase() === 'done';

  // Total minutes of all current subtasks
  const totalSubtasksMinutes = useMemo(() => {
    return (subTasks || []).reduce((acc, st) => acc + parseTimelineMinutes(st.timeline), 0);
  }, [subTasks]);

  // When editing/adding in modal, calculate other subtasks' minutes
  const otherSubtasksMinutes = useMemo(() => {
    if (selectedIdx === null || selectedIdx === undefined) {
      return totalSubtasksMinutes;
    }
    return (subTasks || []).reduce(
      (acc, st, i) => (i === selectedIdx ? acc : acc + parseTimelineMinutes(st.timeline)),
      0
    );
  }, [subTasks, selectedIdx, totalSubtasksMinutes]);

  // Max minutes calculation for inline cell editing on row `index`
  const getSubtaskMaxMinutes = (index) => {
    const mainMinutes = parseTimelineMinutes(mainTaskTimeline);
    const otherMinutes = (subTasks || []).reduce(
      (sum, st, i) => (i === index ? sum : sum + parseTimelineMinutes(st.timeline)),
      0
    );
    const maxSubTask = 8 * 60; // 480 mins max per subtask
    if (!mainMinutes || mainMinutes <= 0) return maxSubTask;
    const remaining = Math.max(0, mainMinutes - otherMinutes);
    return Math.min(maxSubTask, remaining);
  };

  const handleOpenAdd = () => {
    setSelectedIdx(null);
    setModalMode('add');
    setModalOpen(true);
  };

  const handleOpenEdit = (index) => {
    setSelectedIdx(index);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleOpenView = (index) => {
    setSelectedIdx(index);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleDelete = (index) => {
    const target = subTasks[index];
    if (target && String(target.status || '').toLowerCase() === 'done') {
      swal('Warning', 'Completed subtask cannot be deleted.', 'warning');
      return;
    }

    swal({
      title: 'Are you sure?',
      text: `Do you want to delete subtask "${target?.name || `Subtask ${index + 1}`}"?`,
      icon: 'warning',
      buttons: ['Cancel', 'Yes, Delete'],
      dangerMode: true
    }).then((confirmed) => {
      if (confirmed) {
        setSubTasks((prev) => prev.filter((_, i) => i !== index));
        swal('Success', 'Subtask deleted successfully!', 'success');
      }
    });
  };

  const handleInlineTimelineSave = (index, newVal) => {
    let formatted = (newVal || '').trim();
    if (formatted && !formatted.includes('hr')) {
      formatted = `${formatted} hr`;
    }
    setSubTasks((prev) =>
      prev.map((item, i) => (i === index ? { ...item, timeline: formatted } : item))
    );
  };

  const handlePrioritySelect = (newPriority) => {
    if (priorityMenuIndex !== null && priorityMenuIndex !== undefined) {
      setSubTasks((prev) =>
        prev.map((item, i) => (i === priorityMenuIndex ? { ...item, priority: newPriority } : item))
      );
    }
    setPriorityMenuAnchor(null);
    setPriorityMenuIndex(null);
  };

  const handleStatusSelect = (newStatus) => {
    if (statusMenuIndex !== null && statusMenuIndex !== undefined) {
      setSubTasks((prev) =>
        prev.map((item, i) => (i === statusMenuIndex ? { ...item, status: newStatus } : item))
      );
    }
    setStatusMenuAnchor(null);
    setStatusMenuIndex(null);
  };

  const handleSaveSubtask = (subTaskData) => {
    if (selectedIdx !== null && selectedIdx !== undefined) {
      // Edit mode
      setSubTasks((prev) =>
        prev.map((item, i) => (i === selectedIdx ? { ...item, ...subTaskData } : item))
      );
      setModalOpen(false);
      setSelectedIdx(null);
      swal('Success', 'Subtask updated successfully!', 'success');
    } else {
      // Add mode
      const nextNum = subTasks.length + 1;
      const childId = parentTaskId ? `${parentTaskId}.${nextNum}` : String(nextNum);
      const newEntry = {
        id: nextNum,
        parent_id: parentTaskId || '',
        child_id: childId,
        sub_task_id: childId,
        ...subTaskData
      };
      setSubTasks((prev) => [...prev, newEntry]);
      setModalOpen(false);
      setSelectedIdx(null);
      swal('Success', 'Subtask added successfully!', 'success');
    }
  };

  const currentSelectedSubTask = selectedIdx !== null && selectedIdx !== undefined ? subTasks[selectedIdx] : null;
  const hasSubtasks = Array.isArray(subTasks) && subTasks.length > 0;

  // Ensure currently selected subtask's assigned user(s) are always present in the modal user list
  const modalUsers = useMemo(() => {
    if (!currentSelectedSubTask) return users;
    let subDevIds = [];
    if (Array.isArray(currentSelectedSubTask.member_ids)) {
      subDevIds = currentSelectedSubTask.member_ids.map(Number);
    } else if (Array.isArray(currentSelectedSubTask.user_ids)) {
      subDevIds = currentSelectedSubTask.user_ids.map(Number);
    } else if (currentSelectedSubTask.developer_user_id || currentSelectedSubTask.user_id) {
      subDevIds = [Number(currentSelectedSubTask.developer_user_id || currentSelectedSubTask.user_id)];
    }
    if (subDevIds.length === 0) return users;
    const existingIds = new Set((users || []).map((u) => Number(u.id)));
    const missing = [];
    subDevIds.forEach((uid) => {
      const num = Number(uid);
      if (!existingIds.has(num)) {
        const found = (allUsers || []).find((u) => Number(u.id) === num);
        if (found) missing.push(found);
      }
    });
    return missing.length > 0 ? [...users, ...missing] : users;
  }, [users, allUsers, currentSelectedSubTask]);

    const formattedTableData = useMemo(() => {
    return (subTasks || []).map((sub, index) => {
      const subNum = index + 1;
      const priority = sub.priority || 'Medium';
      const status = sub.status || 'Pending';
      const subTaskId = sub.sub_task_id || sub.child_id || (parentTaskId ? `${parentTaskId}.${index + 1}` : `SUB_${String(index + 1).padStart(2, '0')}`);

      // Resolve user display (supports multiple assigned developers)
      let subMemberIds = [];
      if (Array.isArray(sub.member_ids)) {
        subMemberIds = sub.member_ids.map(Number);
      } else if (Array.isArray(sub.user_ids)) {
        subMemberIds = sub.user_ids.map(Number);
      } else if (typeof sub.member_ids === 'string' && sub.member_ids.trim()) {
        try {
          const parsed = JSON.parse(sub.member_ids);
          if (Array.isArray(parsed)) subMemberIds = parsed.map(Number);
        } catch (e) {
          subMemberIds = sub.member_ids.split(',').map((s) => Number(s.trim())).filter(Boolean);
        }
      } else if (sub.developer_user_id || sub.user_id) {
        subMemberIds = [Number(sub.developer_user_id || sub.user_id)];
      }

      const devPool = (Array.isArray(users) && users.length > 0)
        ? (Array.isArray(allUsers) && allUsers.length > 0 ? [...users, ...allUsers] : users)
        : (Array.isArray(allUsers) ? allUsers : []);

      const assignedDevs = [];
      const seenIds = new Set();
      subMemberIds.forEach((uid) => {
        const idNum = Number(uid);
        if (!seenIds.has(idNum)) {
          seenIds.add(idNum);
          const u = devPool.find((d) => Number(d.id) === idNum);
          if (u) assignedDevs.push(u);
        }
      });

      let devName = sub.developer_name || sub.user_name || '';
      let devPhoto = sub.user_photo || null;
      if (!devName && assignedDevs.length === 1) {
        devName = assignedDevs[0].firstName
          ? `${assignedDevs[0].firstName} ${assignedDevs[0].lastName || ''}`.trim()
          : assignedDevs[0].name;
        devPhoto = assignedDevs[0].photo ? resolveImageUrl(assignedDevs[0].photo) : null;
      }

      const rowMaxMinutes = getSubtaskMaxMinutes(index);

      return {
        ...sub,
        id: sub.id !== undefined && sub.id !== null ? sub.id : index,
        _rawIndex: index,
        sub_task_id: (
          <span
            style={{ cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 500, fontSize: 13, color: '#1e293b' }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenView(index);
            }}
            title={`Click to view ${subTaskId}`}
          >
            {subTaskId}
          </span>
        ),
        priority: (
          <div
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={`tm-table-priority-btn tm-priority-${String(priority).toLowerCase()}`}
              onMouseDown={(e) => {
                e.stopPropagation();
                if (!readOnly) {
                  setPriorityMenuAnchor(e.currentTarget);
                  setPriorityMenuIndex(index);
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              style={{ cursor: readOnly ? 'default' : 'pointer' }}
              title="Click to change priority"
            >
              <span>{priority}</span>
              {!readOnly && <ExpandMoreIcon className="tm-priority-arrow-icon" />}
            </button>
          </div>
        ),
        task: (
          <div
            title={sub.name || `Subtask ${subNum}`}
            style={{
              maxWidth: '350px',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              lineHeight: '1.45',
              cursor: 'pointer'
            }}
            onClick={() => handleOpenView(index)}
          >
            <span style={{ fontWeight: 400, color: '#1e293b', fontSize: 13 }}>
              {sub.name || `Subtask ${subNum}`}
            </span>
          </div>
        ),
        assigned_developer: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {assignedDevs.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {assignedDevs.slice(0, 4).map((user, idx) => {
                  const displayName = user.firstName
                    ? `${user.firstName} ${user.lastName || ''}`.trim()
                    : user.name || 'User';
                  const photoSrc = user && user.photo && String(user.photo).trim() !== '' ? resolveImageUrl(user.photo) : defaultAvatar;
                  return (
                    <Tooltip key={user.id || idx} title={displayName} arrow placement="top">
                      <Avatar
                        src={photoSrc}
                        imgProps={{
                          onError: (e) => {
                            if (e.target.src !== defaultAvatar) {
                              e.target.src = defaultAvatar;
                            }
                          }
                        }}
                        style={{
                          width: 24,
                          height: 24,
                          marginLeft: idx === 0 ? 0 : -6,
                          border: '1.5px solid #ffffff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                          backgroundColor: 'transparent',
                          cursor: 'pointer'
                        }}
                      />
                    </Tooltip>
                  );
                })}
                {assignedDevs.length > 4 && (
                  <Tooltip
                    title={`${assignedDevs.length} users assigned (${assignedDevs.map((u) => (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.name)).join(', ')})`}
                    arrow
                    placement="top"
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: '#036dda',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        marginLeft: -6,
                        border: '1.5px solid #ffffff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                        cursor: 'pointer'
                      }}
                    >
                      +{assignedDevs.length - 4}
                    </div>
                  </Tooltip>
                )}
              </div>
            ) : (devName || devPhoto) ? (
              <Tooltip title={devName || 'Assigned Developer'} arrow placement="top">
                <Avatar
                  src={devPhoto || defaultAvatar}
                  imgProps={{
                    onError: (e) => {
                      if (e.target.src !== defaultAvatar) {
                        e.target.src = defaultAvatar;
                      }
                    }
                  }}
                  style={{
                    width: 24,
                    height: 24,
                    border: '1.5px solid #ffffff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    backgroundColor: 'transparent',
                    cursor: 'pointer'
                  }}
                />
              </Tooltip>
            ) : (
              <span style={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>Unassigned</span>
            )}
          </div>
        ),
        timeline: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%'
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <TimelineSegmentedCell
              value={sub.timeline || ''}
              maxHours={8}
              maxMinutes={rowMaxMinutes}
              onSave={(finalVal) => handleInlineTimelineSave(index, finalVal)}
              disabled={readOnly}
            />
          </div>
        ),
        status: (
          <div
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={`tm-table-status-btn tm-status-btn-${String(status).toLowerCase().replace(/\s+/g, '')}`}
              onMouseDown={(e) => {
                e.stopPropagation();
                if (!isBacklogMain && !isDoneMain && !readOnly) {
                  setStatusMenuAnchor(e.currentTarget);
                  setStatusMenuIndex(index);
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              style={{ cursor: (isBacklogMain || isDoneMain || readOnly) ? 'default' : 'pointer' }}
              title={
                isBacklogMain
                  ? 'Locked to Backlog'
                  : isDoneMain
                  ? 'Locked to Done'
                  : 'Click to change status'
              }
            >
              <span>{status === 'Pending' ? 'To Do' : (status || 'To Do')}</span>
              {(!isBacklogMain && !isDoneMain && !readOnly) && (
                <ExpandMoreIcon className="tm-status-arrow-icon" />
              )}
            </button>
          </div>
        )
      };
    });
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [subTasks, parentTaskId, users, allUsers, readOnly, isBacklogMain, isDoneMain]);

return (
    <div
      style={{
        width: '100%',
        border: '1px solid rgba(0, 0, 0, 0.125)',
        borderRadius: 4,
        backgroundColor: '#ffffff',
        overflow: 'hidden'
      }}
    >
      {/* Header bar: matches original Accordion summary styling */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          minHeight: 48,
          backgroundColor: '#ffffff',
          cursor: !hasSubtasks && !readOnly ? 'pointer' : 'default'
        }}
        onClick={() => {
          if (!hasSubtasks && !readOnly) {
            handleOpenAdd();
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Typography
            className="common-form-page-title"
            style={{
              color: 'var(--hrms-primary-blue-end, #036dda)',
              fontSize: 15,
              fontWeight: 400,
              margin: 0
            }}
          >
            {title}
          </Typography>
        </div>

        {/* '+' Icon right where the dropdown icon was */}
        {!readOnly && (
          <Tooltip title="Add Subtask" arrow placement="top">
            <IconButton
              type="button"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenAdd();
              }}
              style={{
                color: '#036dda',
                padding: 6
              }}
            >
              <AddIcon style={{ fontSize: 24 }} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* Body: EXACT TMS Table Component matching Task Management main table (http://localhost:3000/tasks) */}
      {hasSubtasks && (
        <div className="tm-table-view-wrapper" style={{ width: '100%', margin: 0, padding: 0 }}>
          <TableComponent
            tableTh={SUBTASK_TABLE_HEADERS}
            tableData={formattedTableData}
            tableCount={subTasks.length}
            colNameToShow={[
              'sub_task_id',
              'priority',
              'task',
              'assigned_developer',
              'timeline',
              'status'
            ]}
            modelName="Subtasks"
            shellClassName="holiday-table-shell"
            actionBtns={readOnly ? ['view'] : ['view', 'update', 'delete']}
            viewDetails={(row) => handleOpenView(row._rawIndex)}
            openPopUpUpdate={(row) => handleOpenEdit(row._rawIndex)}
            removeRow={(id) => {
              const idx = subTasks.findIndex((s, i) => (s.id !== undefined && s.id !== null ? s.id === id : i === id));
              handleDelete(idx !== -1 ? idx : id);
            }}
            rowsPerPage={10}
            orderBy=""
            order="asc"
          />
        </div>
      )}

      {/* Priority Dropdown Menu */}
      <Menu
        anchorEl={priorityMenuAnchor}
        open={Boolean(priorityMenuAnchor)}
        onClose={() => {
          setPriorityMenuAnchor(null);
          setPriorityMenuIndex(null);
        }}
        disableScrollLock
        getContentAnchorEl={null}
        style={{ zIndex: 2500 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          className: 'tm-priority-menu-paper',
          style: {
            borderRadius: 10,
            minWidth: 140,
            padding: '6px 0',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        {PRIORITY_MENU_OPTIONS.map((opt) => {
          const currentP = priorityMenuIndex !== null && subTasks[priorityMenuIndex]?.priority;
          const isSelected = (currentP || 'Medium') === opt.label;
          return (
            <MenuItem
              key={opt.label}
              onClick={(e) => {
                e.stopPropagation();
                handlePrioritySelect(opt.label);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 16px',
                fontSize: '0.85rem',
                fontWeight: isSelected ? 700 : 500
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: opt.color,
                    display: 'inline-block'
                  }}
                />
                <span>{opt.label}</span>
              </div>
              {isSelected && <CheckIcon style={{ fontSize: 16, color: '#036dda' }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Status Dropdown Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => {
          setStatusMenuAnchor(null);
          setStatusMenuIndex(null);
        }}
        disableScrollLock
        getContentAnchorEl={null}
        style={{ zIndex: 2500 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          className: 'tm-status-menu-paper',
          style: {
            borderRadius: 10,
            minWidth: 150,
            padding: '6px 0',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        {STATUS_MENU_OPTIONS.map((opt) => {
          const currentS = statusMenuIndex !== null && subTasks[statusMenuIndex]?.status;
          const isSelected = (currentS || 'Pending') === opt.label;
          return (
            <MenuItem
              key={opt.label}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusSelect(opt.label);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 16px',
                fontSize: '0.85rem',
                fontWeight: isSelected ? 700 : 500
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: opt.color,
                    display: 'inline-block'
                  }}
                />
                <span>{opt.display || opt.label}</span>
              </div>
              {isSelected && <CheckIcon style={{ fontSize: 16, color: '#036dda' }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* SubTaskModal for Add / Edit / View */}
      {modalOpen && (
        <SubTaskModal
          open={modalOpen}
          mode={modalMode}
          subTask={currentSelectedSubTask}
          users={modalUsers}
          projectName={projectName}
          mainTaskTimeline={mainTaskTimeline}
          otherSubtasksMinutes={otherSubtasksMinutes}
          mainTaskStatus={mainTaskStatus}
          onClose={() => {
            setModalOpen(false);
            setSelectedIdx(null);
          }}
          onSave={handleSaveSubtask}
        />
      )}
    </div>
  );
};

export default SubTasksSection;
